import React from "react";
import { Container, Card, Button } from "../components/ui";
import { TrophyIcon, GlobeIcon, TrendIcon, UsersIcon } from "../components/ui/icons";

const PRINCIPLES = [
  {
    icon: <GlobeIcon />,
    title: "One global list",
    body: "Every ranked player sits on a single worldwide leaderboard, regardless of which country they compete in. Country and club are filters over that list, never separate rankings.",
  },
  {
    icon: <TrophyIcon />,
    title: "Points come from tournaments",
    body: "Ranking points are awarded by finishing position at sanctioned tournaments. The bigger the category, the more points are on the table.",
  },
  {
    icon: <TrendIcon />,
    title: "Your rank is your position",
    body: "Rank is simply where your point total places you among all active players. It is derived from the same list the leaderboard shows, so a profile can never disagree with the rankings page.",
  },
  {
    icon: <UsersIcon />,
    title: "You do not need an account to be ranked",
    body: "Players are entered from tournament sheets and rank immediately. Creating an account lets you claim your existing profile rather than creating a duplicate.",
  },
];

const CATEGORY_POINTS = [
  { round: "Winner", premier: 2000, master: 1500, challenger: 1000 },
  { round: "Final", premier: 1200, master: 900, challenger: 600 },
  { round: "Semi Final", premier: 720, master: 540, challenger: 360 },
  { round: "Quarter Final", premier: 360, master: 270, challenger: 180 },
  { round: "Round of 16", premier: 180, master: 135, challenger: 90 },
  { round: "Round of 32", premier: 90, master: 68, challenger: 45 },
  { round: "Round of 64", premier: 45, master: 34, challenger: 22 },
  { round: "First Round", premier: 10, master: 10, challenger: 10 },
];

const HowRankingsWork = () => (
  <>
    <section className="border-b border-border bg-surface">
      <Container className="py-14 md:py-20">
        <div className="max-w-prose">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">
            The system
          </p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-navy md:text-5xl">
            How PadelRank rankings work
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-muted">
            PadelRank keeps one global ranking of padel players. This page
            explains how a player gets on it, how points are earned, and how
            often the list moves.
          </p>
        </div>
      </Container>
    </section>

    <Container className="py-14 md:py-20">
      <div className="grid gap-5 md:grid-cols-2">
        {PRINCIPLES.map((item) => (
          <Card key={item.title}>
            <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary-wash text-primary">
              {item.icon}
            </span>
            <h2 className="mt-4 text-lg font-semibold text-navy">{item.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">{item.body}</p>
          </Card>
        ))}
      </div>

      <h2 className="mt-16 text-2xl font-bold tracking-tight text-navy">
        Points by round and category
      </h2>
      <p className="mt-2 max-w-prose text-sm text-muted">
        Points are awarded for the round a pair reaches. These are the standard
        tables used across the tour.
      </p>

      <Card padded={false} className="mt-6 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-sm">
            <thead>
              <tr className="border-b border-border bg-bg text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th className="px-5 py-3">Round</th>
                <th className="px-5 py-3 text-right">Premier Padel</th>
                <th className="px-5 py-3 text-right">Master</th>
                <th className="px-5 py-3 text-right">Challenger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {CATEGORY_POINTS.map((row) => (
                <tr key={row.round}>
                  <td className="px-5 py-3 font-medium text-navy">{row.round}</td>
                  <td className="tabular px-5 py-3 text-right text-navy">
                    {row.premier.toLocaleString("en-US")}
                  </td>
                  <td className="tabular px-5 py-3 text-right text-navy">
                    {row.master.toLocaleString("en-US")}
                  </td>
                  <td className="tabular px-5 py-3 text-right text-navy">
                    {row.challenger.toLocaleString("en-US")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-16 rounded-lg bg-navy px-6 py-10 text-center md:px-12 md:py-14">
        <h2 className="text-2xl font-bold text-white md:text-3xl">
          Ready to join the rankings?
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-white/70">
          Create your player profile, compete in tournaments, and climb the
          global rankings.
        </p>
        <Button to="/register" variant="inverse" size="lg" className="mt-6">
          Create Player Profile
        </Button>
      </div>
    </Container>
  </>
);

export default HowRankingsWork;
